import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

import { CLPUser, UserResponse } from '../../models/clpuser.model';
import { eFeatures, eUserRole } from '../../models/enum.model';
import { RoleFeaturePermissions } from '../../models/roleContainer.model';
import {
    eEngageAdvGroupBy,
    eEngageAdvPeriod,
    eEngageAdvRowLevel,
    EngageAdvChannelRow,
    EngageAdvDropDownItem,
    EngageAdvFilter,
    EngageAdvLeadGenRow,
    EngageAdvSummary,
    EngageAdvWindow
} from '../../models/engage-advertising.model';
import { EngageAdvertisingService } from '../../services/engage-advertising.service';
import { GlobalService } from '../../services/global.service';
import { LocalService } from '../../services/shared/local.service';
import { UtilityService } from '../../services/shared/utility.service';

@Component({
    selector: 'app-engage-dashboard',
    templateUrl: './engage-dashboard.component.html',
    styleUrl: './engage-dashboard.component.css'
})
export class EngageDashboardComponent implements OnInit, OnDestroy {
    encryptedUser: string = '';
    userResponse: UserResponse;
    user: CLPUser;
    roleFeaturePermissions: RoleFeaturePermissions;
    showSpinner: boolean = false;

    compareMode: boolean = true;
    period: eEngageAdvPeriod = eEngageAdvPeriod.LastMonth;
    fromDate: string = '';
    toDate: string = '';
    selectedTeams: number[] = [];
    selectedChannels: string[] = [];

    year: number = new Date().getFullYear();
    groupBy: eEngageAdvGroupBy = eEngageAdvGroupBy.Channel;

    teams: EngageAdvDropDownItem[] = [];
    channels: EngageAdvDropDownItem[] = [];
    years: EngageAdvDropDownItem[] = [];

    summary: EngageAdvSummary = null;
    window: EngageAdvWindow = null;
    chartCategories: string[] = [];
    chartValues: number[] = [];
    channelRows: EngageAdvChannelRow[] = [];
    channelTotal: number = 0;
    leadGenRows: EngageAdvLeadGenRow[] = [];
    unavailableColumns: string[] = [];

    loadingSummary: boolean = false;
    loadingChart: boolean = false;
    loadingChannels: boolean = false;
    loadingLeadGen: boolean = false;

    summaryError: string = '';
    chartError: string = '';
    channelsError: string = '';
    leadGenError: string = '';

    errorMsg: string = '';

    noteMsg: string = '';

    expandedMonths: { [monthNumber: number]: boolean } = {};

    readonly rowLevel = eEngageAdvRowLevel;
    readonly groupByChannel = eEngageAdvGroupBy.Channel;
    readonly groupBySite = eEngageAdvGroupBy.Site;

    readonly seriesColor: string = '#4f46e5';

    readonly chartArea = {
        background: 'transparent',
        height: 220,

        margin: { top: 8, right: 18, bottom: 10, left: 26 }
    };

    delta(current: number | null, prior: number | null): number | null {
        if (current == null || prior == null || prior === 0) {
            return null;
        }
        return (current - prior) / prior;
    }

    deltaTone(value: number | null): string {
        if (value == null || value === 0) {
            return 'eng-delta-flat';
        }
        return value > 0 ? 'eng-delta-up' : 'eng-delta-down';
    }

    deltaLabel(value: number | null): string {
        if (value == null) {
            return '';
        }
        const dir = value > 0 ? 'up' : value < 0 ? 'down' : 'unchanged';
        const window = this.summary && this.summary.priorWindow
            ? this.summary.priorWindow.label
            : 'the prior period';
        return dir + ' versus ' + window;
    }

    private readonly channelPalette = ['#4f46e5', '#818cf8', '#c7d2fe', '#0ea5a4',
                                       '#f59e0b', '#94a3b8', '#7c3aed', '#2563eb'];

    channelColor(index: number): string {
        return this.channelPalette[index % this.channelPalette.length];
    }

    channelShare(row: EngageAdvChannelRow): number {
        return this.channelTotal ? (row.leads / this.channelTotal) * 100 : 0;
    }

    pctTone(value: number | null): string {
        if (value == null) {
            return '';
        }
        return value >= 0.03 ? 'eng-chip-good' : value >= 0.015 ? 'eng-chip-mid' : 'eng-chip-low';
    }

    private seq = { summary: 0, chart: 0, channels: 0, leadGen: 0 };

    private rangeTimer: any = null;

    private destroy$ = new Subject<void>();

    constructor(
        public _localService: LocalService,
        private _utilityService: UtilityService,
        private _advertisingService: EngageAdvertisingService,
        private _router: Router,
        private _globalService: GlobalService) {
    }

    ngOnInit(): void {
        this._globalService.getToken((token) => {
            if (token) {
                this.encryptedUser = token;
                this.authenticateR().then(() => {
                    if (this.user) {
                        this.loadFilterOptions().then(() => {
                            this.applyUrlOverrides();
                            this.reload();
                        });
                    } else {
                        this._router.navigate(['/login']);
                    }
                });
            } else {
                this._router.navigate(['/login']);
            }
        });
    }

    ngOnDestroy(): void {
        if (this.rangeTimer) {
            clearTimeout(this.rangeTimer);
            this.rangeTimer = null;
        }
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async authenticateR() {
        this.showSpinner = true;
        await this._localService.authenticateUser(this.encryptedUser, eFeatures.None)
            .then(async (result: UserResponse) => {
                if (result) {
                    await this._localService.checkIfExpiredOrUnauthorized(result?.user);
                    this.userResponse = UtilityService.clone(result);
                    if (this.userResponse?.user) {
                        this.user = this.userResponse?.user;
                        this.roleFeaturePermissions = this.userResponse.roleFeaturePermissions;
                        if (this.user?.userRole <= eUserRole.Administrator) {
                            if (this.roleFeaturePermissions?.view == false) {
                                this._router.navigate(['/unauthorized'], { state: { isMenu: true } });
                            }
                        }
                    }
                }
                this.showSpinner = false;
            })
            .catch((err: HttpErrorResponse) => {
                this._globalService.error('engage-dashboard.authenticateR', err.message, null, 'Features ' + eFeatures.None);
                this.showSpinner = false;
                this._utilityService.handleErrorResponse(err);
            });
    }

    private buildFilter(): EngageAdvFilter {
        return {
            period: this.period,
            useCustomRange: !this.compareMode,
            fromDate: this.compareMode ? '' : this.fromDate,
            toDate: this.compareMode ? '' : this.toDate,
            teamCodes: this.selectedTeams.slice(),
            channels: this.selectedChannels.slice(),
            year: this.year,
            groupBy: this.groupBy
        };
    }

    private async loadFilterOptions(): Promise<void> {
        const res = await this._advertisingService.filterOptions(this.encryptedUser);
        if (!res) {
            return;
        }
        const response = UtilityService.clone(res);
        this.teams = response?.teams ?? [];
        this.channels = response?.channels ?? [];
        this.years = response?.years ?? [];

        if (this.years.length > 0) {
            const first = parseInt(this.years[0].id, 10);
            if (!isNaN(first)) {
                this.year = first;
            }
        }

        const d = response?.defaults;
        if (d) {
            this.compareMode = !d.useCustomRange;
            this.period = d.period === eEngageAdvPeriod.ThisMonth
                ? eEngageAdvPeriod.ThisMonth
                : eEngageAdvPeriod.LastMonth;

            if (d.useCustomRange && d.fromDate && d.toDate) {
                this.fromDate = d.fromDate.substring(0, 10);
                this.toDate = d.toDate.substring(0, 10);
            }

            if (d.teamCode > 0 && this.teams.some(t => t.id === String(d.teamCode))) {
                this.selectedTeams = [d.teamCode];
            }
        }
    }

    private applyUrlOverrides(): void {
        let params: { [key: string]: any } = {};
        try {
            params = this._router.parseUrl(this._router.url).queryParams || {};
        } catch {
            return;
        }

        const from = this.asIsoDay(params['from']);
        const to = this.asIsoDay(params['to']);
        if (from && to && from <= to) {
            this.compareMode = false;
            this.fromDate = from;
            this.toDate = to;
        } else {
            const period = String(params['period'] ?? '').toLowerCase();
            if (period === 'this' || period === 'thismonth') {
                this.compareMode = true;
                this.period = eEngageAdvPeriod.ThisMonth;
            } else if (period === 'last' || period === 'lastmonth') {
                this.compareMode = true;
                this.period = eEngageAdvPeriod.LastMonth;
            }
        }

        const year = parseInt(String(params['year'] ?? ''), 10);
        if (!isNaN(year) && this.years.some(y => y.id === String(year))) {
            this.year = year;
        }
    }

    private asIsoDay(value: any): string {
        const raw = String(value ?? '').trim();
        if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            return null;
        }
        const parts = raw.split('-').map(n => parseInt(n, 10));
        const d = new Date(parts[0], parts[1] - 1, parts[2]);
        if (d.getFullYear() !== parts[0] || d.getMonth() !== parts[1] - 1 || d.getDate() !== parts[2]) {
            return null;
        }
        return raw;
    }

    get selectedChannelName(): string {
        if (!this.selectedChannels.length) {
            return null;
        }
        const id = String(this.selectedChannels[0]);
        const hit = this.channels.find(c => c.id === id);
        return hit ? hit.text : id;
    }

    private fail(section: 'summary' | 'chart' | 'channels' | 'leadGen', message: string): void {
        const text = message || 'The request did not complete.';
        if (section === 'summary') { this.summaryError = text; }
        if (section === 'chart') { this.chartError = text; }
        if (section === 'channels') { this.channelsError = text; }
        if (section === 'leadGen') { this.leadGenError = text; }
        if (!this.errorMsg) { this.errorMsg = text; }
    }

    reload(): void {
        this.errorMsg = '';
        this.noteMsg = '';
        this.summaryError = '';
        this.chartError = '';
        this.channelsError = '';
        this.leadGenError = '';
        const filter = this.buildFilter();

        this.loadSummary(filter);
        this.loadChart(filter);
        this.loadChannels(filter);
        this.loadLeadGen(filter);
    }

    reloadLeadGen(): void {
        this.loadLeadGen(this.buildFilter());
    }

    private async loadSummary(filter: EngageAdvFilter): Promise<void> {
        const mine = ++this.seq.summary;
        this.loadingSummary = true;
        const res = await this._advertisingService.summary(this.encryptedUser, filter);

        if (mine !== this.seq.summary) {
            return;
        }
        this.loadingSummary = false;
        if (!res) {
            this.fail('summary', '');
            return;
        }
        const response = UtilityService.clone(res);
        if (response?.note && !this.noteMsg) {
            this.noteMsg = response.note;
        }
        if (response?.statusCode > 0) {
            this.fail('summary', response?.errorMsg);
            return;
        }
        this.summary = response?.summary ?? null;
        this.window = this.summary?.window ?? null;

        if (response?.errorMsg && !this.errorMsg) {
            this.errorMsg = response.errorMsg;
        }
    }

    readonly chartSeriesLabels = {
        visible: true,
        font: '10px Manrope, Segoe UI, Arial, sans-serif',
        color: '#64748b',
        background: 'transparent',
        position: 'outsideEnd',

        content: (e: any) => (e && e.value ? String(e.value) : '')
    };

    readonly chartValueAxisTitle = {
        text: 'Leads',
        rotation: -90,
        font: '12px Manrope, Segoe UI, Arial, sans-serif',
        color: '#64748b',

        margin: { right: 8 }
    };

    get chartSeriesName(): string {
        return this.window && this.window.name ? this.window.name : 'Leads';
    }

    get chartMajorUnit(): number {
        const peak = this.chartValues.length ? Math.max(...this.chartValues) : 0;
        return peak <= 10 ? 1 : undefined;
    }

    private async loadChart(filter: EngageAdvFilter): Promise<void> {
        const mine = ++this.seq.chart;
        this.loadingChart = true;
        const res = await this._advertisingService.dailyLeads(this.encryptedUser, filter);

        if (mine !== this.seq.chart) {
            return;
        }
        this.loadingChart = false;
        if (!res) {
            this.fail('chart', '');
            return;
        }
        const response = UtilityService.clone(res);
        if (response?.statusCode > 0) {
            this.fail('chart', response?.errorMsg);
            return;
        }
        const series = response?.series ?? [];
        let points = series.length > 0 ? series[0].points ?? [] : [];

        const today = new Date();
        const todayKey = today.getFullYear() + '-'
            + ('0' + (today.getMonth() + 1)).slice(-2) + '-'
            + ('0' + today.getDate()).slice(-2);
        points = points.filter(p => !p.date || p.date.substring(0, 10) <= todayKey);

        this.chartCategories = points.map(p => p.label);
        this.chartValues = points.map(p => p.value);
    }

    private async loadChannels(filter: EngageAdvFilter): Promise<void> {
        const mine = ++this.seq.channels;
        this.loadingChannels = true;
        const res = await this._advertisingService.channels(this.encryptedUser, filter);

        if (mine !== this.seq.channels) {
            return;
        }
        this.loadingChannels = false;
        if (!res) {
            this.fail('channels', '');
            return;
        }
        const response = UtilityService.clone(res);
        if (response?.statusCode > 0) {
            this.fail('channels', response?.errorMsg);
            return;
        }
        this.channelRows = response?.rows ?? [];
        this.channelTotal = response?.total ?? 0;
    }

    private async loadLeadGen(filter: EngageAdvFilter): Promise<void> {
        const mine = ++this.seq.leadGen;
        this.loadingLeadGen = true;
        const res = await this._advertisingService.leadGen(this.encryptedUser, filter);

        if (mine !== this.seq.leadGen) {
            return;
        }
        this.loadingLeadGen = false;
        if (!res) {
            this.fail('leadGen', '');
            return;
        }
        const response = UtilityService.clone(res);
        if (response?.statusCode > 0) {
            this.fail('leadGen', response?.errorMsg);
            return;
        }
        this.leadGenRows = response?.rows ?? [];
        this.unavailableColumns = response?.unavailableColumns ?? [];

        this.expandedMonths = {};
    }

    setCompareMode(useCompare: boolean): void {
        if (this.compareMode === useCompare) {
            return;
        }
        this.compareMode = useCompare;

        if (!useCompare && !this.fromDate && this.window) {
            this.fromDate = this.toIsoDay(this.window.start);
            this.toDate = this.toIsoDay(this.window.end);
        }
        this.reload();
    }

    onPeriodChange(value: string): void {
        this.period = value === '1' ? eEngageAdvPeriod.LastMonth : eEngageAdvPeriod.ThisMonth;
        this.reload();
    }

    onRangeChange(): void {
        if (this.rangeTimer) {
            clearTimeout(this.rangeTimer);
            this.rangeTimer = null;
        }
        if (!this.fromDate || !this.toDate) {
            return;
        }
        this.rangeTimer = setTimeout(() => {
            this.rangeTimer = null;
            this.reload();
        }, 450);
    }

    onTeamChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const value = select.value;
        this.selectedTeams = value === '' ? [] : [parseInt(value, 10)];
        this.reload();
    }

    onChannelChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        this.selectedChannels = select.value === '' ? [] : [select.value];
        this.reload();
    }

    onYearChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        const parsed = parseInt(select.value, 10);
        this.year = isNaN(parsed) ? new Date().getFullYear() : parsed;
        this.reloadLeadGen();
    }

    onGroupByChange(event: Event): void {
        const select = event.target as HTMLSelectElement;
        this.groupBy = select.value === '1' ? eEngageAdvGroupBy.Site : eEngageAdvGroupBy.Channel;
        this.reloadLeadGen();
    }

    private toIsoDay(value: string): string {
        if (!value) {
            return '';
        }
        return value.length >= 10 ? value.substring(0, 10) : value;
    }

    isUnavailable(column: string): boolean {
        return this.unavailableColumns.indexOf(column) >= 0;
    }

    trackByRow(index: number, row: EngageAdvLeadGenRow): string {
        return row.monthNumber + '|' + (row.groupValue ?? '') + '|' + row.level;
    }

    toggleMonth(monthNumber: number): void {
        this.expandedMonths[monthNumber] = !this.expandedMonths[monthNumber];
    }

    isMonthOpen(monthNumber: number): boolean {
        return !!this.expandedMonths[monthNumber];
    }

    isRowVisible(row: EngageAdvLeadGenRow): boolean {
        return row.level !== eEngageAdvRowLevel.Group || this.isMonthOpen(row.monthNumber);
    }

    isAltMonth(row: EngageAdvLeadGenRow): boolean {
        return row.monthNumber % 2 === 0;
    }

    trackByChannel(index: number, row: EngageAdvChannelRow): string {
        return row.channel;
    }

    get isBusy(): boolean {
        return this.loadingSummary || this.loadingChart || this.loadingChannels || this.loadingLeadGen;
    }

    get isEmpty(): boolean {
        return !this.loadingSummary && this.summary != null
            && this.summary.totalLeads === 0 && this.summary.contracts === 0;
    }
}
